import { CHECKS, CATEGORIES, categoryStats, scoreBand, SEVERITY_LABELS, type Finding, type SeverityCounts, type GwCheckCategory } from "./audit-data";
import { buildFindingsSummary, type FindingsSummaryDto } from "./ai-api";
import type { AssistantContext } from "./sections";
import { createGauge } from "../ui/gauge";

export interface SummaryDeps {
  readonly score: number;
  readonly findings: readonly Finding[];
  readonly counts: SeverityCounts;
  // 注入時のみ「AIで分析を見る」ボタンを表示し、PII 非含有の findings サマリを渡す（read-only 相談補助）。
  readonly openAssistant?: (context?: AssistantContext, summary?: FindingsSummaryDto) => void;
}

// 診断はタブ表示ごとに /gw/audit/run を再取得する（常に最新）。手動「再診断」ボタンは設けない（YAGNI）。
export function renderGwSummary(root: HTMLElement, deps: SummaryDeps): void {
  root.replaceChildren();
  const top = document.createElement("div"); top.className = "flex flex-wrap items-center gap-8";
  const gaugeWrap = document.createElement("div"); gaugeWrap.className = "flex flex-col items-center";
  gaugeWrap.append(createGauge({ value: deps.score, label: "ヘルススコア", size: 200 }));
  const band = document.createElement("p"); band.className = "mt-1 text-sm font-medium text-slate-700";
  band.textContent = "評価: " + scoreBand(deps.score);
  gaugeWrap.append(band);
  top.append(gaugeWrap);

  const openAssistant = deps.openAssistant;
  if (openAssistant) {
    const actions = document.createElement("div"); actions.className = "space-y-3";
    const aiBtn = document.createElement("button"); aiBtn.type = "button";
    aiBtn.className = "block rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
    aiBtn.textContent = "AIで分析を見る";
    aiBtn.addEventListener("click", () => openAssistant(undefined, buildFindingsSummary(deps.findings, deps.score)));
    actions.append(aiBtn);
    top.append(actions);
  }

  const sev = document.createElement("div"); sev.className = "mt-6 flex flex-wrap gap-3";
  (["critical", "high", "medium", "low"] as const).forEach((s) => {
    const chip = document.createElement("div"); chip.className = "rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700";
    chip.textContent = SEVERITY_LABELS[s] + " " + deps.counts[s] + " 件"; sev.append(chip);
  });

  const catalogTotalByCategory = (category: GwCheckCategory): number => CHECKS.filter((c) => c.category === category).length;
  const grid = document.createElement("div"); grid.className = "mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4";
  for (const cat of CATEGORIES) {
    const stat = categoryStats(deps.findings, cat.key);
    const card = document.createElement("article"); card.className = "flex flex-col items-center rounded-lg border border-slate-200 p-4 text-center";
    const h = document.createElement("h2"); h.className = "text-sm font-medium text-slate-600"; h.textContent = cat.label; card.append(h);
    const catalogTotal = catalogTotalByCategory(cat.key); const evaluated = stat.total;
    if (evaluated === 0) {
      card.dataset["coverage"] = "none";
      const none = document.createElement("p"); none.dataset["testid"] = "category-unevaluated";
      none.className = "mt-3 flex h-[120px] w-[120px] items-center justify-center rounded-full border border-dashed border-slate-300 text-sm font-medium text-slate-400";
      none.textContent = "未評価";
      const note = document.createElement("p"); note.className = "mt-1 text-xs text-slate-500"; note.textContent = "評価 0 / " + catalogTotal + " 件";
      card.append(none, note);
    } else {
      card.append(createGauge({ value: stat.score, label: cat.label, size: 120, showName: false }));
      const meta = document.createElement("p"); meta.className = "mt-1 text-xs text-slate-500";
      if (evaluated < catalogTotal) card.dataset["coverage"] = "partial";
      meta.textContent = "要対応 " + stat.fail + " 件・評価 " + evaluated + " / " + catalogTotal + " 件";
      card.append(meta);
    }
    grid.append(card);
  }
  root.append(top, sev, grid);
}
